import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import styles from "./buses.module.scss";
import axios from "axios";
import { API_CONSTANTS } from "../../common/constants/api";
import { IBusItem } from "../../common/types";
import { SESSION_KEYS } from "../../common/constants/sessionKeys";

const BusList = () => {
  const [searchParams] = useSearchParams();
  const [fromLoc, setFromLoc] = useState<string | undefined | null>();
  const [toLoc, setToLoc] = useState<string | undefined | null>();
  const [fromLocName, setFromLocName] = useState<string | undefined | null>();
  const [toLocName, setToLocName] = useState<string | undefined | null>();
  const [journeyDate, setJourneyDate] = useState<string | undefined | null>();
  const [buses, setBuses] = useState<IBusItem[]>([]);
  const [onlineBookingClosed, setOnlineBookingClosed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setFromLoc(searchParams.get("from"));
    setToLoc(searchParams.get("to"));
    setJourneyDate(searchParams.get("date"));
  }, [searchParams]);

  useEffect(() => {
    if (fromLoc && toLoc && journeyDate) {
      setIsLoading(true);
      setOnlineBookingClosed(false);
      axios
        .post(API_CONSTANTS.BUS_SEARCH, {
          fromStop: fromLoc,
          toStop: toLoc,
          searchDate: journeyDate,
        })
        .then((res) => {
          setBuses(Array.isArray(res.data) ? res.data : []);
          setOnlineBookingClosed(String(res.headers["x-online-booking-closed"]).toLowerCase() === "true");
        })
        .finally(() => setIsLoading(false));
    }

    setFromLocName(sessionStorage.getItem(SESSION_KEYS.FROM_LOCATION));
    setToLocName(sessionStorage.getItem(SESSION_KEYS.TO_LOCATION));
  }, [fromLoc, toLoc, journeyDate]);

  const formatDate = (dateStr: string | undefined | null) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-IN", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="container pageContainer">
      <div className={styles.routeSummary}>
        <div className={styles.routeInfo}>
          <div className={styles.routeLabel}>Your Journey</div>
          <div className={styles.routePath}>
            <span>{fromLocName || "—"}</span>
            <span className={styles.routeArrow}>→</span>
            <span>{toLocName || "—"}</span>
          </div>
          <div className={styles.routeDate}>
            📅 {formatDate(journeyDate)}
          </div>
        </div>
        <a
          href={`/?from=${fromLoc}&to=${toLoc}&date=${journeyDate}`}
          className={styles.modifyBtn}
        >
          Modify Search
        </a>
      </div>

      {isLoading ? (
        <div className={styles.loadingState}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading buses...</span>
          </div>
          <p className="mt-3 text-muted">Finding available buses...</p>
        </div>
      ) : buses.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>🚌</div>
          <h4>{onlineBookingClosed ? "Online booking closed" : "No buses found"}</h4>
          <p>
            {onlineBookingClosed
              ? "The bus is running on this date, but online booking time has passed."
              : "No buses are available for this route on the selected date. Try a different date or route."}
          </p>
          <a
            href="/"
            className="btn btn-primary mt-3"
          >
            Search Again
          </a>
        </div>
      ) : (
        <>
          <div className={styles.resultsCount}>
            {buses.length} bus{buses.length !== 1 ? "es" : ""} available
          </div>

          <div className={styles.busTableWrapper}>
            <table className={`table ${styles.busTable}`}>
              <thead>
                <tr className={styles.busrowHeader}>
                  <th>Bus Name</th>
                  <th>Departure</th>
                  <th>Type</th>
                  <th>Price</th>
                  <th>Seats</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {buses.map((bus: IBusItem) => (
                  <tr key={bus.busId} className={styles.busrow}>
                    <td>
                      <span className={styles.busName}>{bus.busName}</span>
                    </td>
                    <td>
                      <span className={styles.busTime}>
                        {bus.scheduleTime}
                      </span>
                    </td>
                    <td>
                      <span className={styles.busTypeBadge}>
                        {bus.busType}
                      </span>
                    </td>
                    <td>
                      <span className={styles.busPrice}>₹{bus.price}</span> Onwards
                    </td>
                    <td>
                      <span
                        className={`${styles.seatsAvailable} ${
                          bus.seatsAvailable <= 5
                            ? styles.low
                            : styles.good
                        }`}
                      >
                        {bus.seatsAvailable} left
                      </span>
                    </td>
                    <td>
                      <a
                        href={`/book?busId=${bus.busId}&busRouteMappingId=${bus.busRouteMappingId}&date=${journeyDate}&from=${bus.source}&to=${bus.destination}&busName=${bus.busName}`}
                        className={`btn ${styles.bookBtn} ${
                          bus.seatsAvailable === 0
                            ? "btn-secondary disabled"
                            : "btn-primary"
                        }`}
                      >
                        {bus.seatsAvailable === 0 ? "Sold Out" : "Select Seats"}
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className={styles.busCardWrapper}>
            {buses.map((bus: IBusItem) => (
              <div key={bus.busId} className={styles.busCard}>
                <div className={styles.busCardHeader}>
                  <span className={styles.busCardName}>{bus.busName}</span>
                  <span className={styles.busCardPrice}>₹{bus.price}</span>
                </div>
                <div className={styles.busCardRow}>
                  <span>Departure</span>
                  <strong>{bus.scheduleTime}</strong>
                </div>
                <div className={styles.busCardRow}>
                  <span>Type</span>
                  <span className={styles.busTypeBadge}>{bus.busType}</span>
                </div>
                <div className={styles.busCardRow}>
                  <span>Available Seats</span>
                  <strong
                    className={
                      bus.seatsAvailable <= 5 ? styles.low : styles.good
                    }
                  >
                    {bus.seatsAvailable} left
                  </strong>
                </div>
                <div className={styles.busCardFooter}>
                  <a
                    href={`/book?busId=${bus.busId}&busRouteMappingId=${bus.busRouteMappingId}&date=${journeyDate}&from=${bus.source}&to=${bus.destination}&busName=${bus.busName}`}
                    className={`btn w-100 ${styles.bookBtn} ${
                      bus.seatsAvailable === 0
                        ? "btn-secondary disabled"
                        : "btn-primary"
                    }`}
                  >
                    {bus.seatsAvailable === 0 ? "Sold Out" : "Select Seats"}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default BusList;
